<template>
  <div>
    <!-- Toolbar -->
    <b-button-toolbar class="mb-3">
      <b-button variant="primary" size="sm" :to="{ name: 'UserRegister' }">
        <span class="fas fa-plus mr-1" /> Cadastrar Usuário
      </b-button>
    </b-button-toolbar>

    <!-- Filtros -->
    <b-card no-body class="mb-3">
      <b-card-header
        class="d-flex justify-content-between align-items-center"
        style="cursor: pointer"
        @click="toggleFilters"
      >
        <span>Filtros</span>
        <span
          :class="showFilters ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
        ></span>
      </b-card-header>
      <b-collapse :visible="showFilters">
        <b-card-body>
          <b-form-group label="Buscar por Nome:" label-for="filter-text">
            <b-form-input
              id="filter-text"
              v-model="filterText"
              placeholder="Digite um nome..."
            />
          </b-form-group>
          <b-form-row>
            <b-col md="6">
              <b-form-group label="Filtrar por Tipo:" label-for="filter-role">
                <b-form-select
                  id="filter-role"
                  v-model="filterRole"
                  :options="roleOptions"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="Filtrar por Shopping:"
                label-for="filter-shopping"
              >
                <b-form-select
                  id="filter-shopping"
                  v-model="filterShopping"
                  :options="shoppingOptions"
                />
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-card-body>
      </b-collapse>
    </b-card>

    <!-- Erro -->
    <b-alert v-if="error" show variant="danger">
      <span class="fas fa-exclamation-triangle" /> {{ error }}
    </b-alert>

    <!-- Tabela de usuários -->
    <b-table
      ref="usersList"
      :fields="fields"
      :items="provider"
      striped
      hover
      responsive="sm"
      :busy="isBusy"
      caption-top
      show-empty
    >
      <!-- Loja como badges -->
      <template #cell(store)="{ item }">
        <div class="d-flex flex-wrap">
          <b-badge
            v-for="us in item.userStores"
            :key="us.store.id"
            variant="info"
            class="mr-1 mb-1"
          >
            {{ us.store.name }}
          </b-badge>
        </div>
      </template>

      <!-- Tipo como badges coloridos -->
      <template #cell(role)="{ item }">
        <div class="d-flex flex-wrap">
          <b-badge
            v-for="us in item.userStores"
            :key="us.store.id"
            :variant="getRoleVariant(us.role)"
            class="mr-1 mb-1"
          >
            {{ getRoleText(us.role) }}
          </b-badge>
        </div>
      </template>

      <!-- Status -->
      <template #cell(status)="{ value }">
        <Status :value="value" />
      </template>

      <!-- Ações -->
      <template #cell(actions)="{ item }">
        <BaseListActions
          v-if="canManage(item)"
          :item="item"
          @edit="onEdit"
          @remove="onRemove"
          @update-status="onUpdateStatus"
        />
      </template>

      <!-- Empty state -->
      <div slot="empty" class="text-center">Nenhum usuário cadastrado</div>
    </b-table>
  </div>
</template>

<script>
import services from '@/services'
import withStatusAction from '@/mixins/withStatusAction'
import Status from '@/components/Status'
import BaseListActions from '@/components/BaseListActions'
import { parseHTTPError } from '@/helpers/errors'
import { mapGetters } from 'vuex'

// Definimos ROLES localmente e depois o expomos via data()
const ROLES = {
  ADMIN: { value: 'admin', text: 'Admin Geral' },
  SHOPPING: { value: 'shopping', text: 'Shopping' },
  EMPLOYER: { value: 'employer', text: 'Usuário' },
  ADMIN_FRANCHISEE: { value: 'franchiseeAdmin', text: 'Franqueado Admin' }
}

export default {
  name: 'UsersList',
  components: { Status, BaseListActions },
  mixins: [withStatusAction(services.users.updateUser)],
  data() {
    return {
      isBusy: false,
      error: null,
      showFilters: false,
      filterText: '',
      filterRole: '',
      filterShopping: '',
      allUsers: [],
      ROLES // agora ROLES está disponível no template
    }
  },
  computed: {
    fields() {
      return [
        { key: 'name', label: 'Nome' },
        { key: 'email', label: 'Email' },
        { key: 'store', label: 'Loja' },
        { key: 'status', label: 'Status' },
        { key: 'role', label: 'Tipo' },
        { key: 'actions', label: 'Ações', thStyle: { width: '1%' } }
      ]
    },
    ...mapGetters('store', ['isAdminFranchisee', 'currentStoreId']),
    roleOptions() {
      return [{ value: '', text: 'Todos' }, ...Object.values(this.ROLES)]
    },
    shoppingOptions() {
      const map = {}
      this.allUsers.forEach((user) => {
        user.userStores.forEach((us) => {
          if (us.store && us.store.id) map[us.store.id] = us.store.name
        })
      })
      const opts = Object.keys(map).map((id) => ({ value: id, text: map[id] }))
      return [{ value: '', text: 'Todos' }, ...opts]
    }
  },
  watch: {
    filterText: 'refreshTable',
    filterRole: 'refreshTable',
    filterShopping: 'refreshTable'
  },
  methods: {
    getRoleText(roleValue) {
      const entry = Object.values(this.ROLES).find((r) => r.value === roleValue)
      return entry ? entry.text : roleValue
    },
    getRoleVariant(roleValue) {
      if (roleValue === this.ROLES.ADMIN.value) return 'danger'
      if (roleValue === this.ROLES.ADMIN_FRANCHISEE.value) return 'warning'
      return 'secondary'
    },
    toggleFilters() {
      this.showFilters = !this.showFilters
    },
    refreshTable() {
      if (this.$refs.usersList) this.$refs.usersList.refresh()
    },
    async provider() {
      this.isBusy = true
      try {
        let users = await services.users.fetchUsers()
        if (this.isAdminFranchisee && this.currentStoreId) {
          users = users.filter(
            (u) =>
              u.userStores.length === 1 &&
              u.userStores[0].storeId === this.currentStoreId &&
              ['employer', 'franchiseeAdmin'].includes(u.userStores[0].role)
          )
        }
        this.allUsers = users

        if (this.filterText) {
          const txt = this.filterText.toLowerCase()
          users = users.filter((u) => u.name.toLowerCase().includes(txt))
        }
        if (this.filterRole) {
          users = users.filter((u) =>
            u.userStores.some((us) => us.role === this.filterRole)
          )
        }
        if (this.filterShopping) {
          const shopId = parseInt(this.filterShopping, 10)
          users = users.filter((u) =>
            u.userStores.some((us) => us.store && us.store.id === shopId)
          )
        }
        return users
      } catch (err) {
        this.error = parseHTTPError(err)
        return []
      } finally {
        this.isBusy = false
      }
    },
    canManage(user) {
      if (this.isAdminFranchisee) {
        return user.userStores.some((us) => us.role === 'employer')
      }
      return true
    },
    onEdit(user) {
      this.$router.push({ name: 'UserRegister', params: { id: user.id } })
    },
    async onRemove(user) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Remover usuário ${user.name}?`
      )
      if (confirmed) {
        await services.users.removeUser(user.id)
        this.$refs.usersList.refresh()
        this.$root.$toast.success('Usuário removido com sucesso')
      }
    },
    async onUpdateStatus(user) {
      await this.toggleStatus(user)
      this.$refs.usersList.refresh()
    }
  }
}
</script>

<style scoped>
/* Ajustes de estilo, se desejar */
</style>
